// src/components/common/AvatarLabel.jsx
import React, { useMemo } from 'react';
import PersonAvatar from './avatar/PersonAvatar';

const AVATAR_SIZE_PX = {
  sm: 32,
  md: 40,
  lg: 56,
};

const normalizeAvatarGender = (gender) => {
  const value = String(gender || '').trim().toLowerCase();
  if (['male', 'man', 'm', '男性', '男'].includes(value)) return 'male';
  if (['female', 'woman', 'f', '女性', '女'].includes(value)) return 'female';
  return 'unknown';
};

/**
 * props:
 * - name: 氏名
 * - gender: 'male' | 'female' | 'secret' | null
 * - imageKey: プロフィール画像ファイル名（null可）
 * - shortPart: 例) 'Vn' のようなショート名（null可）
 * - size: 'sm' | 'md' | 'lg'  (デフォ: 'md')
 * - variant: 画像プロキシのバリアント（未指定時は表示サイズに応じて自動選択）
 * - className: 外側ラッパに追加クラス
 */
export default function AvatarLabel({
  name,
  gender,
  imageKey,
  shortPart,
  size = 'md',
  variant = undefined,
  className = '',
  topBadge = null,
  topBadgeClassName = '',
  onClick = null,
}) {
  const normalizedGender = useMemo(() => normalizeAvatarGender(gender), [gender]);
  const bgClass = useMemo(() => {
    switch (normalizedGender) {
      case 'male': return 'avatar--male';
      case 'female': return 'avatar--female';
      case 'secret': return 'avatar--secret';
      default: return 'avatar--unknown';
    }
  }, [normalizedGender]);

  const clickable = typeof onClick === 'function';

  return (
    <div
      className={`avatar-label ${className}${clickable ? ' avatar-label--clickable' : ''}`.trim()}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
    >
      <PersonAvatar
        role="artist"
        gender={normalizedGender}
        name={name}
        profilePicture={imageKey}
        variant={variant}
        size={AVATAR_SIZE_PX[size] || AVATAR_SIZE_PX.md}
        className={`avatar ${bgClass} avatar--${size}`}
        imageClassName="avatar__img"
        initialClassName="avatar__initials"
      />
      <div className="avatar-label__text">
        {topBadge ? (
          <span className={`avatar-label__badge ${topBadgeClassName}`.trim()}>
            {topBadge}
          </span>
        ) : null}
        <div className="avatar-label__name-line">
          <span className="avatar-label__name">{name || '—'}</span>
          {shortPart ? <span className="avatar-label__part">（{shortPart}）</span> : null}
        </div>
      </div>
    </div>
  );
}
